import {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Select from 'react-select';
import {Scrollbar} from 'react-scrollbars-custom';
import {useTranslation} from 'react-i18next';
import {newSelectStyles} from '../../common/shared/select.styled';
import {SwitchStyled} from '../../common/shared/switch.styled';
import Button from '../../common/Button';
import BllinkUtils from "../../common/utils/utils";

import listItemBg from '../../images/newDesign/list_item_bg.svg';
import {StyledNotifications} from './notifications.styled';
import bell from '../../images/newDesign/bell.svg';
import TenantDetailsModal from './TenantDetailsModal';
import {useAccessToken} from '../../common/hooks/useAccessToken';
import {hideLoader, showLoader} from '../../store/common/slice';
import {fetchBuildingComments, fetchAllComments} from '../../store/comments/actions';
import {BllinkLogger} from '../../common/utils/bllink_loggers';
import {fetchManagerBuildingDataLocal, loadDebtList} from '../../store/buildings/actions';
import CacheData from '../../common/utils/cacheData';
import { constants } from '../../common/constants/constants';
import {Link} from 'react-router-dom';
import WarningLetter from '../reports/debt_list/warningLetterNew';
import WarningLetterModal from './WarningLetterModal';
import { fileToBase64 } from '../../../utils/fileToBase64';
import { createPdfFromHtml } from '../../common/shared/statelessMethods';

const BllinkTime = BllinkUtils.BllinkTime;

export default function Notifications() {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const userProfile = useSelector((state) => state.auth.userProfile);
    const allComments = useSelector((state) => state.comments.allComments);
    const buildingComments = useSelector((state) => state.comments.buildingComments);
    const debtListByBuildings = useSelector((state) => state.buildings.debtListByBuildings);
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const client = useAccessToken();

    const [showTenantsData, setShowTenantsData] = useState(false);
    const [selectedApartmentData, setSelectedApartmentData] = useState({});
    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState({});
    const [buildingsData, setBuildingsData] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [commentsData, setCommentsData] = useState([]);
    const [warningLetterData, setWarningLetterData] = useState({});
    const [sendLetterModal, setSendLetterModal] = useState(false);
    const [currentModalData, setCurrentModalData] = useState({
        apartmentNumber: '',
        buildingId: ''
    });
    const [checkboxes, setCheckboxes] = useState({
        owner: false,
        renter: false
    });
    const [letterInfo, setLetterInfo] = useState({
        address: '',
        fileBase64: ''
    });

    useEffect(() => {
        (async function () {
            if (!client) return;
            dispatch(showLoader());

            let {buildings} = await client.get('/managers/buildings');
            BllinkLogger.info(`all buildings for user are `, buildings);
            if (buildings) {
                const buildingsOptions = buildings.map(building => ({
                    value: building.buildingID,
                    label: building.title,
                    disabled: building.disabled
                }));

                setBuildings(buildingsOptions);
                setSelectedBuilding(buildingsOptions[0]);
                await dispatch(fetchBuildingComments({buildingID: buildingsOptions[0].value, options: {notify: true, ack: false}}));
            }

            dispatch(hideLoader());
        })();
    }, [client]);

    useEffect(async () => {
        if (userProfile.userFirstName != null) {
            if (showAll) {
                dispatch(showLoader());
                if (allComments.length) {
                    setCommentsData(allComments)
                } else {
                    // ack=false - only new notifications which was not read
                    // notify=true - if comment is notification
                    await loadNotifications();
                }
                dispatch(hideLoader());
            }
        }
    }, [showAll, userProfile]);


    const loadNotifications = async () => {
        let partnerID = (userProfile?.partnerID) || '';
        partnerID = partnerID.toLowerCase();
        const shouldMock = constants.demoBuildingIDS.includes(partnerID);
        if (shouldMock){
            await loadMockedNotifications(partnerID);
        }else {
            await loadCurrentNotificationFromServer();
        }
    }

    const loadMockedNotifications = async (partnerID) => {
        // load dynamically to exclude from common bundle
        const module = await import('../../common/mocks/specificBuildingsMockMapping');
        const getSpecificMockDataForPartner = module.getSpecificMockDataForPartner;
        const data = getSpecificMockDataForPartner(partnerID, 'notifications');
        if (data) {
            await setCommentsData(data);
        }else{
            await loadCurrentNotificationFromServer();
        }

    }

    const loadCurrentNotificationFromServer = async () => {
        await dispatch(fetchAllComments({notify: true, ack: false}));
    }

    useEffect(() => {
        if (showAll) {
            setCommentsData(allComments)
        } else setCommentsData(buildingComments);
    }, [allComments, buildingComments]);

    useEffect(() => {
        (async function () {
            if (commentsData?.comments?.length) {
                let promises = [];
                Array.isArray(commentsData.BuildingId) ? promises = commentsData.BuildingId.map(id => getBuildingData(id)) : promises.push(getBuildingData(commentsData.BuildingId));
                const data = await Promise.allSettled(promises);
                setBuildingsData(data.map(promise => promise.value).reduce((acc, val) => ({ ...acc, [val.buildingId]: val.result}), {}));
            }
        })();
    }, [commentsData]);

    const onBuildingChange = async selectedBuildingObj => {
        dispatch(showLoader());
        setShowAll(false);
        setSelectedBuilding(selectedBuildingObj);

        await dispatch(fetchBuildingComments({buildingID: selectedBuildingObj.value, options: {notify: true, ack: false}}));

        dispatch(hideLoader());
    }

    const getBuildingData = async buildingId => {
        const result = await CacheData.getOrCache(async () => {
                const {payload} = await dispatch(fetchManagerBuildingDataLocal({buildingID: buildingId}));
                return payload?.building;
            },
            'managerData',
            {buildingID: buildingId}
        );
        return {
            buildingId,
            result
        };
    }

    const getWarningLetter = async (letterContainer, address) => {
        dispatch(showLoader());

        const file = await createPdfFromHtml(letterContainer, '', {save: false, ignoreLastPage: false});
        const fileBase64 = await fileToBase64(file);
        setLetterInfo({
            address,
            fileBase64
        });

        dispatch(hideLoader());
    }

    const sendOrOpenModalWarningLetter = async (buildingId, apartmentNumber) => {
        dispatch(showLoader());
        let buildingDebt = debtListByBuildings[buildingId];
        if (!buildingDebt) {
            const {payload} = await dispatch(loadDebtList({buildingID: buildingId, options: {start: 0, end: 100}}));
            buildingDebt = payload.result;
        }
        const apartmentDebt = buildingDebt.find(item => item.apartmentNumber === apartmentNumber);
        setWarningLetterData(apartmentDebt || {});
        dispatch(hideLoader());
        if (apartmentDebt?.apartmentNumber) {
            setCurrentModalData({
                buildingId,
                apartmentNumber
            });
            showSendLetterModal();
        }
    }

    const sendWarningLetterFromModal = async () => {
        dispatch(showLoader());
        const { buildingId, apartmentNumber } = currentModalData;
        const { address, fileBase64 } = letterInfo;
        const fileName = `${t('letter.fileName')}_${address}_${apartmentNumber}.pdf`;
        const paymentLink = `${constants.frontendUrl}/${constants.pages.paymentSelectionUrl}/${buildingId}/${apartmentNumber}`;
        const { owner, renter } = checkboxes;
        const toOwner = owner;
        const toRenter = renter;
        await client.managers.operations.sendWarningLetter(buildingId, apartmentNumber, paymentLink, 'application/pdf', fileName, fileBase64, toOwner, toRenter);
        setWarningLetterData({});
        dispatch(hideLoader());

        hideSendLetterModal();
    }

    const getTenantDetails = (evt, commentData) => {
        evt.preventDefault();
        setShowTenantsData(true);

        const data = buildingsData[commentData.buildingId];
        setSelectedApartmentData({
            ...commentData,
            ...data?.tenants[commentData.apartmentNumber]
        });
    }

    const getApartmentData = apartmentId => {
        return commentsData.ApartmentData[apartmentId];
    }

    const hideSendLetterModal = () => {
        setSendLetterModal(false);
        setCheckboxes({
            owner: false,
            renter: false
        });
    };

    const showSendLetterModal = () => setSendLetterModal(true);

    return (
        <StyledNotifications ltr={ltr}>
            <h1 className={'page-title'}>
                <img src={bell} alt="bell" />
                <span>{t('notifications.title')}</span>
            </h1>

            <div className={'notifications'}>
                <div className={'notifications-actions'}>
                    <h3>{t('notifications.chooseBuilding')}</h3>
                    <Select
                        className={'select'}
                        styles={newSelectStyles}
                        options={buildings}
                        value={selectedBuilding}
                        onChange={onBuildingChange}
                        isRtl={!ltr}
                    />
                    <div className={'switch'}>
                        <span>{t('notifications.showAll')}</span>
                        <SwitchStyled
                            checked={showAll}
                            onChange={() => setShowAll(!showAll)}
                        />
                    </div>
                </div>

                {commentsData?.comments?.length ? (
                    <Scrollbar
                        style={{ width: '100%', height: 'calc(100vh - 330px)' }}
                        trackYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-track" />;
                            }
                        }}
                        thumbYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                            }
                        }}
                        rtl={false}
                        noScrollX
                    >
                        <ul className={'notifications-list'}>
                            {
                                commentsData.comments.map(item => (
                                    <li key={item.id}>
                                        <div className={'bg'}>
                                            <img src={listItemBg} alt="bg" />
                                        </div>
                                        <div className={'item-content'}>
                                            <div className={'data'}>
                                                <div className={'address'}>
                                                    <span>{t('notifications.apartment')} {getApartmentData(item.CommentLink.entityId).ApartmentNum}</span>
                                                    <span className={'street'}>{buildingsData[getApartmentData(item.CommentLink.entityId).PtoPId]?.title}</span>
                                                </div>
                                                <a
                                                    href=""
                                                    className={'apt-details'}
                                                    onClick={e => getTenantDetails(e, {
                                                        buildingId: getApartmentData(item.CommentLink.entityId).PtoPId,
                                                        apartmentNumber: getApartmentData(item.CommentLink.entityId).ApartmentNum
                                                    })}
                                                >
                                                    {t('notifications.more_apartment_details')}
                                                </a>
                                                <span className={'date'}>{BllinkTime.parseDate(item.createdAt)}</span>
                                                <div className={'name'}>
                                                    <span>{item.User.firstName}</span>
                                                    <span>{item.User.lastName}</span>
                                                </div>
                                            </div>
                                            <p>{item.text}</p>
                                            <div className={'actions'}>
                                                <Link
                                                    to={`/${constants.pages.tenantDetailsManagerUrl}/${buildingsData[getApartmentData(item.CommentLink.entityId).PtoPId]?.buildingID}`}
                                                >
                                                    <Button
                                                        type={'button'}
                                                        small
                                                        outlined
                                                        rounded={false}
                                                    >
                                                        {t('notifications.recordPayment')}
                                                    </Button>
                                                </Link>

                                                <Link
                                                    to={`/${constants.pages.tenantDetailsManagerUrl}/${buildingsData[getApartmentData(item.CommentLink.entityId).PtoPId]?.buildingID}`}
                                                >
                                                    <Button
                                                        type={'button'}
                                                        small
                                                        outlined
                                                        rounded={false}
                                                    >
                                                        {t('notifications.charge')}
                                                    </Button>
                                                </Link>

                                                <Button
                                                    type={'button'}
                                                    small
                                                    outlined
                                                    rounded={false}
                                                    onClick={() => sendOrOpenModalWarningLetter(buildingsData[getApartmentData(item.CommentLink.entityId).PtoPId]?.buildingID, getApartmentData(item.CommentLink.entityId).ApartmentNum)}
                                                >
                                                    {t('notifications.sendWarningLetter')}
                                                </Button>

                                                <Button
                                                    type={'button'}
                                                    small
                                                    outlined
                                                    rounded={false}
                                                    disabled
                                                >
                                                    {t('notifications.sendRequest')}
                                                </Button>
                                            </div>
                                        </div>
                                    </li>
                                ))
                            }
                        </ul>
                    </Scrollbar>
                ) : (
                    <h3>{t('notifications.emptyList')}</h3>
                )}
            </div>
            <TenantDetailsModal
                show={showTenantsData}
                onHide={() => setShowTenantsData(false)}
                tenantData={selectedApartmentData}
            />

            <WarningLetterModal
                show={sendLetterModal}
                onHide={hideSendLetterModal}
                checkboxes={checkboxes}
                setCheckboxes={setCheckboxes}
                onSend={sendWarningLetterFromModal}
            />

            {
                warningLetterData.apartmentNumber && (
                    <WarningLetter
                        row={warningLetterData}
                        clearSelectedRow={() => setWarningLetterData({})}
                        getWarningLetter={getWarningLetter}
                    />
                )
            }
        </StyledNotifications>
    );
}
